import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { fromEvent } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { NotificationItem } from '../../models/notification-item.model';
import { NotificationService } from '../../services/notification.service';
import { NotificationApi } from '../../services/notification-api.service';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent extends BaseComponent implements OnInit, OnDestroy {
  @Output() closeNotificationClicked = new EventEmitter();

  notifications: NotificationItem[] = [];

  offset = 0;
  limit = 10;

  hasMoreNotifications = true;

  get unreadCount$() {
    return this.notificationService.unreadCount$;
  }

  constructor(
    private notificationService: NotificationService,
    private notificationApi: NotificationApi
  ) {
    super();
  }

  ngOnInit(): void {
    this.getAllNotifications({ offset: this.offset, limit: this.limit });
    this.notificationService.refreshUnreadCount();

    // Realtime arrivals go on top while the panel is open.
    this.notificationService.notification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => this.addNotifications([notification], true));

    // After a reconnect, backfill whatever arrived while offline.
    fromEvent(window, 'online')
      .pipe(
        switchMap(() => this.notificationApi.getAllNotification({ offset: 0, limit: this.limit })),
        takeUntil(this.destroy$)
      )
      .subscribe((notifications) => this.addNotifications(notifications, true));
  }

  getAllNotifications(body: { offset: number; limit: number }) {
    // Full spinner on the first page; later pages keep the list visible.
    this.load(this.notificationApi.getAllNotification(body), {
      isLoadingTransparent: body.offset > 0,
    }).subscribe((notifications) => {
      this.addNotifications(notifications);
      this.hasMoreNotifications = notifications.length === this.limit;
    });
  }

  // UI Events
  onNotificationClicked(notification: NotificationItem): void {
    this.notificationService.markAsRead([notification]).subscribe();
    // Closed first: a chat target opens in this same drawer, which the close would otherwise shut.
    this.closeNotificationClicked.emit();
    this.notificationService.navigateToNotificationTarget(notification);
  }

  markAllAsRead(): void {
    this.load(this.notificationService.markAllAsRead(this.notifications), {
      fetchOnce: true,
      isLoadingTransparent: true,
    }).subscribe();
  }

  loadMore(): void {
    this.offset += 1;
    this.getAllNotifications({ offset: this.offset, limit: this.limit });
  }

  trackById(_: number, notification: NotificationItem): string {
    return notification.id;
  }

  // private methods
  // Skips ids already listed, so realtime, backfill and paging never duplicate a row.
  private addNotifications(incoming: NotificationItem[], prepend = false): void {
    // FCM delivers ids as strings, the API may not.
    const ids = new Set(this.notifications.map((notification) => String(notification.id)));
    const fresh = incoming.filter((notification) => !ids.has(String(notification.id)));
    this.notifications = prepend ? [...fresh, ...this.notifications] : [...this.notifications, ...fresh];
  }
}
